import { useEffect, useMemo, useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import Grid from '@rover/icons/svg/grid.svg';
import PlayGlyph from '@rover/icons/svg/play-glyph.svg';
import { Box, Button, Flex, Image } from '@rover/kibble/core';
import { Badge } from '@rover/kibble/official-patterns/Badge';
import DSTokenMap from '@rover/kibble/styles/DSTokenMap';
import type { Image as ImageType } from '@rover/rsdk/src/apiClient/latest/model/image';
import { PersonServiceAreas } from '@rover/rsdk/src/apiClient/latest/model/personServiceAreas';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import {
  PLAY_BADGE_OPACITY,
  PLAY_BADGE_SIZE,
} from '../../../../../components/ImageGallery/constants';
import type { GalleryVideoItem } from '../../../../../components/ImageGallery/ImageGallery.common';
import {
  countGalleryVideoItems,
  galleryItemKey,
  isGalleryVideoItem,
} from '../../../../../components/ImageGallery/ImageGallery.common';
import { GALLERY_PREVIEW_IMAGE_COUNT } from '../../../../../components/images/PhotoGallery/constants';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';
import RevampDistributionWidget from '../RevampDistributionWidget/RevampDistributionWidget';

import {
  SitterGalleryLocationExpandEvent,
  SitterGalleryReviewCardClickedEvent,
} from './analyticsEvents';
import ServiceAreaMap from './ServiceAreaMap';
import {
  BORDER_RADIUS,
  getFirstColumnCornerRadius,
  getFourImageCornerRadius,
  getSecondColumnCornerRadius,
  getTwoImageCornerRadius,
} from './utils';

const TRANSITION_DURATION = '0.3s';
const TRANSITION_EASING = 'ease';
const TRANSITION_STAGGER_DELAY = '0.15s';
const cssTransition = (...props: string[]): string =>
  props.map((prop) => `${prop} ${TRANSITION_DURATION} ${TRANSITION_EASING}`).join(', ');

const TRANSITION_COLLAPSE_TOTAL_MS = 400;
const staggeredColumnTransition = (isCollapsed: boolean): string =>
  isCollapsed
    ? `opacity 0.2s ${TRANSITION_EASING} 0s, max-width ${TRANSITION_DURATION} ${TRANSITION_EASING} 0.1s`
    : `opacity ${TRANSITION_DURATION} ${TRANSITION_EASING} ${TRANSITION_STAGGER_DELAY}, max-width ${TRANSITION_DURATION} ${TRANSITION_EASING} 0s`;

/**
 * Returns `true` only after `delayMs` of `active` being `true`; resets to
 * `false` immediately when `active` becomes `false`. Used to defer `display:none`
 * until a collapse CSS transition has fully completed.
 */
const useDelayedFlag = (active: boolean, delayMs: number): boolean => {
  const [flag, setFlag] = useState(active);
  useEffect(() => {
    if (active) {
      const timer = setTimeout(() => setFlag(true), delayMs);
      return () => clearTimeout(timer);
    }
    setFlag(false);
    return undefined;
  }, [active, delayMs]);
  return flag;
};

const PLAY_BADGE_SIZE_PX = `${PLAY_BADGE_SIZE}px`;
const PLAY_BADGE_BACKGROUND = DSTokenMap.TEXT_COLOR_PRIMARY.withOpacity(PLAY_BADGE_OPACITY);
const PLAY_BADGE_FILL = `${DSTokenMap.TEXT_COLOR_PRIMARY_INVERSE}`;

type GalleryBlockItem = ImageType | GalleryVideoItem;

type ViewAllConfig = {
  imageCount: number;
  onViewAllPress: () => void;
  hideOverlay: boolean;
  display: string[];
  showCount?: boolean;
};

type LayoutProps = {
  previewImages: GalleryBlockItem[];
  imageCount: number;
  onImagePress: (index: number) => void;
  // eslint-disable-next-line react/no-unused-prop-types
  onViewAllPress: () => void; // Not all layouts show view all (e.g., single image)
  // eslint-disable-next-line react/no-unused-prop-types
  hideOverlay: boolean; // Not all layouts show view all (e.g., single image)
  // eslint-disable-next-line react/no-unused-prop-types
  mapExpanded: boolean; // Not used by all layouts
};

type GallerySize = 'large' | 'small';

type GalleryImageProps = {
  image: GalleryBlockItem;
  index: number;
  imageCount: number;
  onPress: (index: number) => void;
  borderRadius?: string | string[];
  eager?: boolean;
  size?: GallerySize;
};

const SRC_FALLBACKS: Record<GallerySize, (image: ImageType) => string> = {
  large: (image) => image.galleryMain || image.galleryThumb || image.gallerySmall || image.small,
  small: (image) => image.galleryThumb || image.galleryMain || image.gallerySmall || image.small,
};

const PlayBadge = (): JSX.Element => (
  <Flex
    position="absolute"
    top="0"
    width="100%"
    height="100%"
    justifyContent="center"
    alignItems="center"
    data-testid="gallery-play-icon"
    sx={{ pointerEvents: 'none' }}
  >
    <Flex
      width={PLAY_BADGE_SIZE_PX}
      height={PLAY_BADGE_SIZE_PX}
      borderRadius="round"
      justifyContent="center"
      alignItems="center"
      sx={{ backgroundColor: PLAY_BADGE_BACKGROUND }}
    >
      <PlayGlyph height={PLAY_BADGE_SIZE_PX} width={PLAY_BADGE_SIZE_PX} fill={PLAY_BADGE_FILL} />
    </Flex>
  </Flex>
);

const VideoTitle = (): JSX.Element => {
  const { t } = useLingui();

  return (
    <Flex position="absolute" bottom="2x" left="2x">
      <Badge
        text={t`Intro video`}
        boxProps={{ background: 'overlay' }}
        textProps={{ textColor: 'primary_inverse' }}
      />
    </Flex>
  );
};

const GalleryImage = ({
  image,
  index,
  imageCount,
  onPress,
  borderRadius,
  eager,
  size = 'small',
}: GalleryImageProps): JSX.Element => {
  const { t } = useLingui();
  const photoNumber = index + 1;

  const isVideo = isGalleryVideoItem(image);

  const src = isVideo ? image.small : SRC_FALLBACKS[size](image);

  const label = isVideo
    ? image.video.description
    : image.caption || t`Photo ${photoNumber} of ${imageCount}`;

  return (
    <Box
      position="relative"
      flex="1"
      height="100%"
      width="100%"
      p="0x"
      borderRadius={borderRadius}
      border="none"
      bg="transparent"
      sx={{
        overflow: 'hidden',
        cursor: 'pointer',
        textAlign: 'inherit',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': { transform: 'scale(1.02)' },
      }}
      webAs="button"
      webProps={{ 'aria-label': label }}
      onPress={() => onPress(index)}
    >
      <Image
        height="100%"
        width="100%"
        webProps={{
          src,
          ...(eager && { eager: true }),
        }}
        resizeMethod="cover"
        // The button's aria-label provides the accessible name; image is decorative
        description=""
      />
      {isVideo && (
        <>
          <PlayBadge />
          <VideoTitle />
        </>
      )}
    </Box>
  );
};

const ViewAllButton = ({
  imageCount,
  onViewAllPress,
  hideOverlay,
  display,
  showCount = false,
}: ViewAllConfig): JSX.Element => {
  const { t } = useLingui();

  return (
    <Flex
      position="absolute"
      bottom="4x"
      right="4x"
      display={display}
      sx={{
        visibility: hideOverlay ? 'hidden' : 'visible',
        opacity: hideOverlay ? 0 : 1,
        transition: `opacity ${TRANSITION_DURATION} ${TRANSITION_EASING}, visibility ${TRANSITION_DURATION} ${TRANSITION_EASING}`,
      }}
    >
      <Button
        size="default"
        icon={Grid}
        onPress={onViewAllPress}
        aria-label={t`View all ${imageCount} photos`}
      >
        {showCount ? t`View all ${imageCount}` : t`View all`}
      </Button>
    </Flex>
  );
};

const RatingsDistribution = (): JSX.Element => {
  const { t } = useLingui();
  const { setReviewsModalOpen } = useRevampProfileContext();

  return (
    <Box
      flex="1"
      width="100%"
      background="caution"
      borderRadius={[
        BORDER_RADIUS.BOTTOM_RIGHT,
        BORDER_RADIUS.BOTTOM,
        BORDER_RADIUS.BOTTOM,
        BORDER_RADIUS.BOTTOM,
      ]}
      border="none"
      p="0x"
      sx={{ overflow: 'hidden', cursor: 'pointer', textAlign: 'inherit' }}
      data-testid="gallery-block-ratings-slot"
      webAs="button"
      webProps={{ 'aria-description': t`View ratings and reviews` }}
      onPress={() => {
        emitAnalyticsEvent(new SitterGalleryReviewCardClickedEvent());
        setReviewsModalOpen(true);
      }}
    >
      <Box display={['flex', 'flex', 'flex', 'none']} height="100%">
        <RevampDistributionWidget variant="gallerySm" />
      </Box>
      <Box display={['none', 'none', 'none', 'flex']} height="100%">
        <RevampDistributionWidget variant="galleryLg" />
      </Box>
    </Box>
  );
};

type HeroSlotProps = {
  image: GalleryBlockItem;
  imageCount: number;
  flex: string | string[];
  borderRadius: string | string[];
  onPress: (index: number) => void;
  viewAll?: ViewAllConfig;
  width?: string | string[];
};

const HeroSlot = ({
  image,
  imageCount,
  flex,
  borderRadius,
  onPress,
  viewAll,
  width,
}: HeroSlotProps): JSX.Element => (
  <Box
    flex={flex}
    position="relative"
    borderRadius={borderRadius}
    width={width}
    sx={{ overflow: 'hidden', transition: cssTransition('flex') }}
  >
    <GalleryImage
      image={image}
      index={0}
      imageCount={imageCount}
      onPress={onPress}
      eager
      size="large"
    />
    {viewAll && <ViewAllButton {...viewAll} />}
  </Box>
);

type ImageColumnProps = {
  images: GalleryBlockItem[];
  startIndex: number;
  imageCount: number;
  onPress: (index: number) => void;
  cornerRadiusFn: (index: number) => string | string[] | undefined;
  display: string[];
  viewAll?: ViewAllConfig & { onIndex: number };
  width?: string | string[];
  opacity?: number | number[];
  maxWidth?: string | string[];
  sx?: Record<string, unknown>;
  size?: GallerySize;
};

const ImageColumn = ({
  images,
  startIndex,
  imageCount,
  onPress,
  cornerRadiusFn,
  display,
  viewAll,
  width,
  opacity,
  maxWidth,
  sx,
  size = 'small',
}: ImageColumnProps): JSX.Element => (
  <Flex
    flexDirection="column"
    flex={width ? 'none' : '1'}
    width={width}
    gap="2x"
    display={display}
    opacity={opacity}
    maxWidth={maxWidth}
    sx={sx}
  >
    {images.map((image, i) => (
      <Box key={galleryItemKey(image)} position="relative" flex="1" minHeight={0}>
        <GalleryImage
          image={image}
          index={startIndex + i}
          imageCount={imageCount}
          onPress={onPress}
          borderRadius={cornerRadiusFn(i)}
          size={size}
        />
        {viewAll && viewAll.onIndex === i && <ViewAllButton {...viewAll} />}
      </Box>
    ))}
  </Flex>
);

const SingleImageLayout = ({
  previewImages,
  imageCount,
  onImagePress,
}: LayoutProps): JSX.Element => (
  <HeroSlot
    image={previewImages[0]}
    imageCount={imageCount}
    flex={['1', '3', '3', '3']}
    borderRadius={[BORDER_RADIUS.TOP, BORDER_RADIUS.ALL, BORDER_RADIUS.ALL, BORDER_RADIUS.ALL]}
    onPress={onImagePress}
  />
);

const TwoImageLayout = ({
  previewImages,
  imageCount,
  onImagePress,
  onViewAllPress,
  hideOverlay,
}: LayoutProps): JSX.Element => (
  <Flex flexDirection="row" gap="2x" minHeight={0}>
    <HeroSlot
      image={previewImages[0]}
      imageCount={imageCount}
      flex={['1', '2', '2', '2']}
      borderRadius={[BORDER_RADIUS.TOP, BORDER_RADIUS.LEFT, BORDER_RADIUS.LEFT, BORDER_RADIUS.LEFT]}
      onPress={onImagePress}
      viewAll={{
        imageCount,
        onViewAllPress,
        hideOverlay,
        display: ['flex', 'none', 'none', 'none'],
      }}
    />
    <Box
      position="relative"
      flex="2"
      display={['none', 'flex', 'flex', 'flex']}
      sx={{ overflow: 'hidden' }}
    >
      <GalleryImage
        image={previewImages[1]}
        index={1}
        imageCount={imageCount}
        onPress={onImagePress}
        borderRadius={getTwoImageCornerRadius(1)}
        size="large"
      />
    </Box>
  </Flex>
);

const ThreeImageLayout = ({
  previewImages,
  imageCount,
  onImagePress,
  onViewAllPress,
  hideOverlay,
  mapExpanded,
}: LayoutProps): JSX.Element => (
  <>
    <HeroSlot
      image={previewImages[0]}
      imageCount={imageCount}
      flex={['1', '2', mapExpanded ? '2' : '1', mapExpanded ? '2' : '1']}
      borderRadius={[BORDER_RADIUS.TOP, BORDER_RADIUS.LEFT, BORDER_RADIUS.LEFT, BORDER_RADIUS.LEFT]}
      onPress={onImagePress}
      viewAll={{
        imageCount,
        onViewAllPress,
        hideOverlay,
        display: ['flex', 'none', 'none', 'none'],
      }}
    />

    <Flex
      flexDirection="column"
      flex={['initial', mapExpanded ? '1' : '2', '1', '1']}
      gap="2x"
      display={['none', 'flex', 'flex', 'flex']}
      sx={{ transition: cssTransition('flex') }}
    >
      <Box position="relative" flex="1" minHeight={0}>
        <GalleryImage
          image={previewImages[1]}
          index={1}
          imageCount={imageCount}
          onPress={onImagePress}
          borderRadius={getFourImageCornerRadius('top')}
          size="large"
        />
      </Box>
      <Box position="relative" flex="1" minHeight={0}>
        <GalleryImage
          image={previewImages[2]}
          index={2}
          imageCount={imageCount}
          onPress={onImagePress}
          borderRadius={getFourImageCornerRadius('bottom-right')}
          size="large"
        />
      </Box>
    </Flex>
  </>
);

const FourImageLayout = ({
  previewImages,
  imageCount,
  onImagePress,
  onViewAllPress,
  hideOverlay,
  mapExpanded,
}: LayoutProps): JSX.Element => {
  const photoRemoved = useDelayedFlag(mapExpanded, TRANSITION_COLLAPSE_TOTAL_MS);
  return (
    <>
      <HeroSlot
        image={previewImages[0]}
        imageCount={imageCount}
        flex={['1', '2', mapExpanded ? '2' : '1', mapExpanded ? '2' : '1']}
        borderRadius={[
          BORDER_RADIUS.TOP,
          mapExpanded ? BORDER_RADIUS.ALL : BORDER_RADIUS.LEFT,
          BORDER_RADIUS.LEFT,
          BORDER_RADIUS.LEFT,
        ]}
        onPress={onImagePress}
        viewAll={{
          imageCount,
          onViewAllPress,
          hideOverlay,
          display: ['flex', mapExpanded ? 'flex' : 'none', 'none', 'none'],
        }}
      />

      {/* S: stacked column (2 images) */}
      <ImageColumn
        images={previewImages.slice(1, 3)}
        startIndex={1}
        imageCount={imageCount}
        onPress={onImagePress}
        cornerRadiusFn={(i) => getFirstColumnCornerRadius(i, 2)}
        display={['none', mapExpanded ? 'none' : 'flex', 'none', 'none']}
        opacity={mapExpanded ? 0 : 1}
        viewAll={{
          imageCount,
          onViewAllPress,
          hideOverlay,
          display: ['none', 'flex', 'none', 'none'],
          onIndex: 1,
        }}
        sx={{
          overflow: 'hidden',
          transition: staggeredColumnTransition(mapExpanded),
        }}
      />

      {/* M/L + XL: 1-over-2 grid */}
      <Flex flexDirection="column" flex="1" gap="2x" display={['none', 'none', 'flex', 'flex']}>
        <Box position="relative" flex="1" minHeight={0}>
          <GalleryImage
            image={previewImages[1]}
            index={1}
            imageCount={imageCount}
            onPress={onImagePress}
            borderRadius={getFourImageCornerRadius('top')}
            size="large"
          />
        </Box>
        <Flex flexDirection="row" gap={mapExpanded ? '0' : '2x'} flex="1" minHeight={0}>
          <Box position="relative" flex="1">
            <GalleryImage
              image={previewImages[2]}
              index={2}
              imageCount={imageCount}
              onPress={onImagePress}
              borderRadius={
                mapExpanded
                  ? getFourImageCornerRadius('bottom')
                  : getFourImageCornerRadius('bottom-left')
              }
              size="small"
            />
            {mapExpanded && (
              <ViewAllButton
                imageCount={imageCount}
                onViewAllPress={onViewAllPress}
                hideOverlay={hideOverlay}
                display={['none', 'flex', 'flex', 'flex']}
              />
            )}
          </Box>
          <Box
            position="relative"
            flex={mapExpanded ? '0' : '1'}
            opacity={mapExpanded ? 0 : 1}
            display={photoRemoved ? 'none' : 'flex'}
            sx={{
              overflow: 'hidden',
              transition: cssTransition('flex', 'opacity'),
            }}
          >
            <GalleryImage
              image={previewImages[3]}
              index={3}
              imageCount={imageCount}
              onPress={onImagePress}
              borderRadius={getFourImageCornerRadius('bottom-right')}
              size="small"
            />
          </Box>
        </Flex>
      </Flex>
    </>
  );
};

const StackedColumnsLayout = ({
  previewImages,
  imageCount,
  onImagePress,
  onViewAllPress,
  hideOverlay,
  mapExpanded,
}: LayoutProps): JSX.Element => {
  const smallImages = previewImages.slice(1);
  const lastFirstColumnIndex = Math.min(smallImages.length, 2) - 1;

  // `imageCount` counts photos, `previewImages` counts tiles, and the intro
  // video occupies a tile without being a photo. Comparing the two bases
  // directly would hide "View all" from a sitter whose photo count exactly
  // fills the preview, stranding the photo the video displaced.
  const previewPhotoCount = previewImages.length - countGalleryVideoItems(previewImages);
  const hasUnshownPhotos = imageCount > previewPhotoCount;

  const columnRemoved = useDelayedFlag(mapExpanded, TRANSITION_COLLAPSE_TOTAL_MS);

  return (
    <>
      <HeroSlot
        image={previewImages[0]}
        imageCount={imageCount}
        flex={['1', 'none', 'none', 'none']}
        width={['auto', '378px', '378px', '465.5px']}
        borderRadius={[
          BORDER_RADIUS.TOP,
          mapExpanded ? BORDER_RADIUS.ALL : BORDER_RADIUS.LEFT,
          BORDER_RADIUS.LEFT,
          BORDER_RADIUS.LEFT,
        ]}
        onPress={onImagePress}
        viewAll={{
          imageCount,
          onViewAllPress,
          hideOverlay,
          display: ['flex', mapExpanded ? 'flex' : 'none', 'none', 'none'],
        }}
      />

      <ImageColumn
        images={smallImages.slice(0, 2)}
        startIndex={1}
        imageCount={imageCount}
        onPress={onImagePress}
        cornerRadiusFn={(i) => getFirstColumnCornerRadius(i, smallImages.length, mapExpanded)}
        display={['none', columnRemoved ? 'none' : 'flex', 'flex', 'flex']}
        width={['auto', '185px', '185px', '221px']}
        opacity={[1, mapExpanded ? 0 : 1, 1, 1]}
        maxWidth={['none', mapExpanded ? '0px' : '185px', 'none', 'none']}
        viewAll={{
          imageCount,
          onViewAllPress,
          hideOverlay,
          display: ['none', 'flex', mapExpanded ? 'flex' : 'none', mapExpanded ? 'flex' : 'none'],
          onIndex: smallImages.length === 1 ? 0 : lastFirstColumnIndex,
        }}
        sx={{
          overflow: 'hidden',
          transition: staggeredColumnTransition(mapExpanded),
        }}
      />

      {smallImages.length > 2 && (
        <ImageColumn
          images={smallImages.slice(2, 4)}
          startIndex={3}
          imageCount={imageCount}
          onPress={onImagePress}
          cornerRadiusFn={(i) => getSecondColumnCornerRadius(i)}
          display={[
            'none',
            'none',
            columnRemoved ? 'none' : 'flex',
            columnRemoved ? 'none' : 'flex',
          ]}
          width={['auto', '185px', '185px', '221px']}
          opacity={mapExpanded ? 0 : 1}
          maxWidth={mapExpanded ? '0px' : '221px'}
          viewAll={
            hasUnshownPhotos
              ? {
                  imageCount,
                  onViewAllPress,
                  hideOverlay,
                  display: ['none', 'none', 'flex', 'flex'],
                  showCount: true,
                  onIndex: smallImages.length <= 3 ? 0 : 1,
                }
              : undefined
          }
          sx={{
            overflow: 'hidden',
            transition: staggeredColumnTransition(mapExpanded),
          }}
        />
      )}
    </>
  );
};

const LAYOUTS: Record<number, React.FC<LayoutProps>> = {
  1: SingleImageLayout,
  2: TwoImageLayout,
  3: ThreeImageLayout,
  4: FourImageLayout,
};

type GalleryBlockProps = {
  images: GalleryBlockItem[];
  imageCount: number;
  onImagePress: (index: number) => void;
  onViewAllPress: () => void;
  hideOverlay?: boolean;
  mapData: PersonServiceAreas | undefined;
  locationLabel: string | null;
  isMapLoading: boolean;
  serviceType: string | undefined;
  serviceProfileType?: string;
};

const GalleryBlock = ({
  images,
  imageCount,
  onImagePress,
  onViewAllPress,
  hideOverlay = false,
  mapData,
  locationLabel,
  isMapLoading,
  serviceType,
  serviceProfileType,
}: GalleryBlockProps): JSX.Element | null => {
  const { t } = useLingui();
  const [mapExpanded, setMapExpanded] = useState(false);
  const mapFullyExpanded = useDelayedFlag(mapExpanded, TRANSITION_COLLAPSE_TOTAL_MS);

  const previewImages = useMemo(() => images.slice(0, GALLERY_PREVIEW_IMAGE_COUNT), [images]);

  const videoIndex = useMemo(() => previewImages.findIndex(isGalleryVideoItem), [previewImages]);
  const videoItem = videoIndex === -1 ? undefined : previewImages[videoIndex];

  if (images.length === 0) return null;

  const Layout = LAYOUTS[previewImages.length] ?? StackedColumnsLayout;

  const isStackedLayout = !LAYOUTS[previewImages.length];
  const expandedImageFlex = mapExpanded ? '3' : '4';
  const imageBoxFlex = isStackedLayout
    ? ['initial', 'none', 'none', 'none']
    : ['initial', expandedImageFlex, expandedImageFlex, expandedImageFlex];
  const expandedSidebarFlex = mapExpanded ? '2' : '1';
  const sidebarFlex = isStackedLayout
    ? ['initial', '1', '1', '1']
    : ['initial', expandedSidebarFlex, expandedSidebarFlex, expandedSidebarFlex];

  return (
    <Flex
      data-testid="gallery-block"
      flexDirection={['column', 'row', 'row', 'row']}
      height={['auto', '378px', '378px', '466px']}
      width="100%"
      webProps={{ role: 'region', 'aria-label': t`Photo gallery` }}
    >
      <Flex
        flexDirection={['column', 'row']}
        gap="2x"
        height={[mapExpanded ? '0px' : '226px', 'auto']}
        opacity={[mapExpanded ? 0 : 1, 1]}
        mb={[mapExpanded ? '0x' : '2x', '0x']}
        mr={['0x', mapExpanded && isStackedLayout ? '0x' : '2x']}
        flex={imageBoxFlex}
        sx={{
          transition: cssTransition('flex', 'height', 'opacity', 'margin-bottom'),
          visibility: [mapFullyExpanded ? 'hidden' : 'visible', 'visible'],
        }}
      >
        <Layout
          previewImages={previewImages}
          imageCount={imageCount}
          onImagePress={onImagePress}
          onViewAllPress={onViewAllPress}
          hideOverlay={hideOverlay}
          mapExpanded={mapExpanded}
        />
      </Flex>

      <Flex
        flexDirection={['row', 'column', 'column', 'column']}
        gap={mapExpanded ? '0x' : '2x'}
        flex={sidebarFlex}
        ml={mapExpanded && Layout === StackedColumnsLayout ? '2x' : '0x'}
        height={[mapExpanded ? '374px' : '140px', 'auto']}
        sx={{ transition: cssTransition('flex', 'height', 'gap') }}
      >
        <ServiceAreaMap
          expanded={mapExpanded}
          onToggleExpand={() =>
            setMapExpanded((prev) => {
              if (!prev) emitAnalyticsEvent(new SitterGalleryLocationExpandEvent());
              return !prev;
            })
          }
          mapData={mapData}
          locationLabel={locationLabel}
          isLoading={isMapLoading}
          serviceType={serviceType}
          serviceProfileType={serviceProfileType}
        />
        <Flex
          flexDirection="column"
          flex={mapExpanded ? '0' : '1'}
          minHeight="0"
          opacity={mapExpanded ? 0 : 1}
          sx={{
            overflow: 'hidden',
            transition: cssTransition('opacity', 'flex'),
            visibility: mapFullyExpanded ? 'hidden' : 'visible',
          }}
        >
          {/* A sitter with no gallery photos gets the video at index 0, where it
              already holds the mweb hero — taking this slot too would show the
              same tile twice. */}
          {videoItem && videoIndex > 0 ? (
            <>
              <Box display={['none', 'flex', 'flex', 'flex']} flex="1" minHeight={0}>
                <RatingsDistribution />
              </Box>
              <Box
                display={['flex', 'none', 'none', 'none']}
                flex="1"
                minHeight={0}
                data-testid="gallery-block-video-slot"
              >
                <GalleryImage
                  image={videoItem}
                  index={videoIndex}
                  imageCount={imageCount}
                  onPress={onImagePress}
                  borderRadius={BORDER_RADIUS.BOTTOM_RIGHT}
                />
              </Box>
            </>
          ) : (
            <RatingsDistribution />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default GalleryBlock;
